import { useCallback, useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useUserRef } from '@univerus/plugin-products';
import {
  readRecentDocs,
  recentDocEntityRef,
  rememberDoc,
  RECENT_DOCS_CHANGED,
  type RecentDoc,
  type RecentDocRef,
} from './recentDocs';

/** A history entry with the title and description the catalog gives now. */
export interface ShownRecentDoc extends RecentDoc {
  title: string;
  description?: string;
}

/**
 * The signed-in user's stored docs history, kept in step with storage.
 * Undefined until identity resolves.
 */
function useStoredDocs(): RecentDoc[] | undefined {
  const userRef = useUserRef();
  const [docs, setDocs] = useState<RecentDoc[]>();
  useEffect(() => {
    if (!userRef) return undefined;
    const refresh = () => setDocs(readRecentDocs(userRef));
    refresh();
    window.addEventListener('storage', refresh);
    window.addEventListener(RECENT_DOCS_CHANGED, refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener(RECENT_DOCS_CHANGED, refresh);
    };
  }, [userRef]);
  return docs;
}

/**
 * The signed-in user's recently viewed docs, titled from the catalog in one
 * request. Entities the catalog no longer returns (deleted, or no longer
 * visible to this user) are left out. Undefined until the first lookup
 * settles; later lookups keep the current list until they do. On a catalog
 * error, an empty list, so the rest of the history still shows.
 */
export function useRecentDocs(): ShownRecentDoc[] | undefined {
  const catalogApi = useApi(catalogApiRef);
  const stored = useStoredDocs();
  const [shown, setShown] = useState<ShownRecentDoc[]>();
  useEffect(() => {
    if (!stored) return undefined;
    if (!stored.length) {
      setShown([]);
      return undefined;
    }
    let active = true;
    catalogApi
      .getEntitiesByRefs({
        entityRefs: stored.map(recentDocEntityRef),
        fields: ['metadata.name', 'metadata.title', 'metadata.description'],
      })
      .then(({ items }) => {
        if (!active) return;
        setShown(
          stored.flatMap((doc, i) => {
            const entity = items[i];
            if (!entity) return [];
            return [
              {
                ...doc,
                title: entity.metadata.title || entity.metadata.name,
                description: entity.metadata.description || undefined,
              },
            ];
          }),
        );
      })
      .catch(() => {
        if (active) setShown([]);
      });
    return () => {
      active = false;
    };
  }, [catalogApi, stored]);
  return shown;
}

/**
 * Records a docs view in the signed-in user's history. A no-op until
 * identity resolves.
 */
export function useRememberDoc(): (doc: RecentDocRef) => void {
  const userRef = useUserRef();
  return useCallback(
    doc => {
      if (userRef) rememberDoc(userRef, doc);
    },
    [userRef],
  );
}
